import ViewLeavePageMenu from 'domain/people/leave/Show/ViewLeavePageMenu';
import LeaveEntityHeader from 'domain/people/leave/shared/LeaveEntityHeader';
import SingleLeavesBreadcrumbs from 'domain/people/leave/shared/SingleLeavesBreadcrumbs';
import EditLeaveButton from 'domain/people/leave/shared/button/EditLeaveButton';
import RejectLeaveButton from '../shared/button/RejectLeaveButton';
import { AddLeaveCompensationButton, isLeaveCompensationButtonVisible } from 'domain/shared/leave';
import LeavePayoutOptionField from 'domain/people/leave/shared/field/LeavePayoutOptionField';
import LeaveWorkerField from 'domain/people/leave/shared/field/LeaveWorkerField';
import LeaveDurationField from 'domain/people/leave/shared/field/LeaveDurationField';
import useLeaveService, { hasSignatureOption } from 'domain/people/leave/shared/leaveService';
import RulesViolationReasonField from 'domain/people/leave/shared/field/RulesViolationReasonField';
import { BusinessRulesListField } from 'domain/people/shared/formRules';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import SignatureOptionField from '../shared/field/SignatureOptionField';
import ElectronicOptionAgreementField from '../shared/field/ElectronicOptionAgreementField';
import useLeavesErrorMap from '../createupdate/errorHandlers';
import {
  LEAVE_TYPES_ALIASES,
  IS_PHYSICAL_OPTION_ALIASES,
  LEAVE_UPDATE,
  LEAVE_READ_FULL_DATA,
  LEAVE_READ_REQUEST_INFORMATION,
  LEAVE_EXTEND,
} from '../../../shared/leave/constants';
import { useAuthorization } from 'framework/authorization';
import { DateField, TextField } from 'framework/ui/field';
import { useLeaveId } from '../leaveRouting';
import { LeaveDtoWithCompensation } from '../../../shared/leave/types';
import { Show } from 'framework/show';
import { ShowSection, SectionRow, ShowPageLayout } from 'framework/ui/layout';
import { Data } from 'framework/types';
import LeaveStatusField from '../shared/field/LeaveStatusField';
import LeaveTypeField from '../shared/field/LeaveTypeField';
import BusinessRulesViolatedField from '../shared/input/BusinessRulesViolatedField';

const ViewLeave = ({ isInfoView, leaveId }: { isInfoView?: boolean; leaveId?: string }) => {
  const id = useLeaveId();
  const { getByIdWithExpensesInfo, canBeExtended } = useLeaveService();
  const { isGranted } = useAuthorization();

  const content = (
    <>
      <ShowSection title="Leave Information">
        <SectionRow>
          <LeaveWorkerField source="employeeAlias" label="Worker" />
          <LeaveTypeField source="type" label="Type" />
          <LeaveStatusField source="status" label="Status" />
          <LeavePayoutOptionField
            source="payoutOption"
            label="Payout option"
            isVisible={(leave: Data<LeaveDtoWithCompensation>) =>
              leave.getValueBySource('type') === LEAVE_TYPES_ALIASES.PAID_LEAVE && isGranted(LEAVE_READ_FULL_DATA)
            }
          />
        </SectionRow>
        <SectionRow>
          <DateField source="period.startDate" label="Start date" />
          <DateField source="period.actualEndDate" label="End date" />
          <LeaveDurationField source="period.duration" label="Duration" />
        </SectionRow>
        <SectionRow>
          <AttachmentsListField
            source="artifactIds"
            label="Application scans"
            isVisible={() => isGranted(LEAVE_READ_FULL_DATA)}
          />
        </SectionRow>
      </ShowSection>

      <ShowSection isVisible={isGranted(LEAVE_READ_REQUEST_INFORMATION)} title="Leave request information">
        <SectionRow>
          <BusinessRulesViolatedField
            source="rulesViolationInfo.violatedBusinessRules"
            label="Business rules violated"
          />
          <BusinessRulesListField
            source="rulesViolationInfo.violatedBusinessRules"
            label="Violated business rules"
            isVisible={(leave: Data<LeaveDtoWithCompensation>) =>
              !!leave.getValueBySource('rulesViolationInfo.violatedBusinessRules') &&
              !!leave.getValueBySource('rulesViolationInfo.violatedBusinessRules').length
            }
            errorMap={useLeavesErrorMap()}
          />
          <RulesViolationReasonField
            source="rulesViolationInfo.rulesViolationReason"
            label="Reasons for business rules violation"
          />
          <TextField source="rulesViolationInfo.comment" label="Description" />
        </SectionRow>
      </ShowSection>
      <ShowSection
        title="Signature option"
        isVisible={leave => hasSignatureOption(leave) && isGranted(LEAVE_READ_FULL_DATA)}
      >
        <SectionRow>
          <SignatureOptionField
            source="signatureOption.option"
            label="Worker will be able to arrive at the office for signing documents"
          />
          <TextField
            source="signatureOption.electronicOptionReason"
            label="Reason why worker will not be able to arrive at the office"
            isVisible={(data: any) =>
              data.getValueBySource('signatureOption.option') === IS_PHYSICAL_OPTION_ALIASES.ELECTRONIC
            }
          />
          <ElectronicOptionAgreementField
            source="signatureOption.electronicOptionAgreement"
            label="Worker confirms that the company can use a comment in the dedicated Redmine ticket as a replacement for their physical signature in leave documents"
            isVisible={(data: any) =>
              data.getValueBySource('signatureOption.option') === IS_PHYSICAL_OPTION_ALIASES.ELECTRONIC
            }
          />
        </SectionRow>
      </ShowSection>
    </>
  );

  return (
    <Show getDataMethod={() => getByIdWithExpensesInfo(leaveId || id!)}>
      {isInfoView ? (
        content
      ) : (
        <ShowPageLayout
          buttons={[
            <EditLeaveButton
              key="edit-leave"
              source="id"
              icon={<i className="fa fa-edit" />}
              isVisible={leave => isGranted(LEAVE_UPDATE) || (isGranted(LEAVE_EXTEND) && canBeExtended(leave))}
              permissionToCheck={null}
            />,
            <RejectLeaveButton key="reject-leave" />,
          ]}
          menu={<ViewLeavePageMenu />}
          breadcrumbs={<SingleLeavesBreadcrumbs />}
          entityHeader={<LeaveEntityHeader />}
          headerButtons={[<AddLeaveCompensationButton isVisible={isLeaveCompensationButtonVisible} />]}
        >
          {content}
        </ShowPageLayout>
      )}
    </Show>
  );
};

export default ViewLeave;
